import {
  GET_PROJECTS_BY_ORGANIZATION_ID,
  useLazyGetProjectsByOrganizationId,
} from "../operations/queries/getProjectsByOrganizationId";
import ProjectsList from "../components/ProjectsList";
import { useAddProject } from "../operations/mutations/addProject";
import { useUpdateProject } from "../operations/mutations/updateProject";
import { useDeleteProject } from "../operations/mutations/deleteProject";
import { useAddPermitRegister } from "../operations/mutations/addPermitRegister";
import { useUpdatePermitRegister } from "../operations/mutations/updatePermitRegister";
import { useDeletePermitRegister } from "../operations/mutations/deletePermitRegister";
import { useSetMaxProjectAssignments } from "../operations/mutations/setMaxProjectAssignments";
import { useLazyGetPermitTypesByOrganizationId } from "../operations/queries/getPermitTypesByOrganizationId";
import { useEffect, useState } from "react";
import { EmergencyContact } from "../models/EmergencyContact";
import { EsriIntegrationTypes } from "../models/EsriIntegrationType";
import { GeoJSONPolygonType } from "../models/GeoJSONPolygonType";
import { useAppContext } from "../hooks/useAppContext";
import { removeTypenameDeep } from "../utils/apollo";

const ProjectsListContainer = () => {
  const { currentOrganization } = useAppContext();
  const [searchString, setSearchString] = useState("");

  const {
    getProjectsByOrganizationId: getProjects,
    data: projects,
    status: getProjectsStatus,
  } = useLazyGetProjectsByOrganizationId();
  const { mutate: addProjectMutate, status: addProjectStatus } = useAddProject();
  const { mutate: updateProjectMutate, status: updateProjectStatus } = useUpdateProject();
  const { mutate: deleteProjectMutate, status: deleteProjectStatus } = useDeleteProject();
  const { mutate: addPermitRegisterMutate, status: addPermitRegisterStatus } = useAddPermitRegister();
  const { mutate: updatePermitRegisterMutate, status: updatePermitRegisterStatus } = useUpdatePermitRegister();
  const { mutate: deletePermitRegisterMutate, status: deletePermitRegisterStatus } = useDeletePermitRegister();
  const { mutate: setMaxProjectAssignmentsMutate, status: setMaxStatus } = useSetMaxProjectAssignments();
  const { getPermitTypesByOrganizationId: getPermitTypes, data: permitTypesData } =
    useLazyGetPermitTypesByOrganizationId();

  const handleSearchProjects = (searchString: string) => {
    if (searchString !== undefined) setSearchString(searchString);
  };

  useEffect(() => {
    if (currentOrganization) {
      getProjects({
        variables: { organizationId: currentOrganization.id, searchString },
      });
      getPermitTypes({ variables: { organizationId: currentOrganization.id } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization, searchString]);

  const handleAddProject = (
    name: string,
    number: string,
    location: string,
    emergencyContact: EmergencyContact,
    boundaries?: GeoJSONPolygonType,
  ) => {
    if (currentOrganization) {
      addProjectMutate({
        refetchQueries: [
          {
            query: GET_PROJECTS_BY_ORGANIZATION_ID,
            variables: { organizationId: currentOrganization.id, searchString },
          },
        ],
        variables: {
          name,
          number,
          location,
          boundaries: removeTypenameDeep(boundaries),
          emergencyContact,
          organizationId: currentOrganization.id,
        },
      });
    }
  };

  const handleEditProject = (
    id: string,
    name: string,
    number: string,
    location: string,
    emergencyContact: EmergencyContact,
    boundaries?: GeoJSONPolygonType,
  ) => {
    if (currentOrganization) {
      updateProjectMutate({
        variables: {
          projectId: id,
          organizationId: currentOrganization.id,
          name,
          number,
          location,
          boundaries: removeTypenameDeep(boundaries),
          emergencyContact,
        },
      });
    }
  };

  const handleDeleteProject = (id: string) => {
    if (currentOrganization) {
      deleteProjectMutate({
        refetchQueries: [
          {
            query: GET_PROJECTS_BY_ORGANIZATION_ID,
            variables: { organizationId: currentOrganization.id, searchString },
          },
        ],
        variables: {
          projectId: id,
          organizationId: currentOrganization.id,
        },
      });
    }
  };

  const handleAddPermitRegister = (title: string, description: string, projectId: string, permitTypeIds: string[]) => {
    if (currentOrganization) {
      addPermitRegisterMutate({
        refetchQueries: [
          {
            query: GET_PROJECTS_BY_ORGANIZATION_ID,
            variables: { organizationId: currentOrganization.id, searchString },
          },
        ],
        variables: { title, description, projectId, organizationId: currentOrganization.id, permitTypeIds },
      });
    }
  };

  const handleUpdatePermitRegister = (id: string, title: string, description: string, permitTypeIds: string[]) => {
    if (currentOrganization) {
      updatePermitRegisterMutate({
        refetchQueries: [
          {
            query: GET_PROJECTS_BY_ORGANIZATION_ID,
            variables: { organizationId: currentOrganization.id, searchString },
          },
        ],
        variables: { permitRegisterId: id, title, description, permitTypeIds },
      });
    }
  };

  const handleDeletePermitRegister = (id: string) => {
    if (currentOrganization) {
      deletePermitRegisterMutate({
        refetchQueries: [
          {
            query: GET_PROJECTS_BY_ORGANIZATION_ID,
            variables: { organizationId: currentOrganization.id, searchString },
          },
        ],
        variables: { permitRegisterId: id },
      });
    }
  };

  const handleSetMaxProjectAssignments = (projectId: string, max: number | null) => {
    if (!currentOrganization) return;

    setMaxProjectAssignmentsMutate({
      variables: { projectId, max, organizationId: currentOrganization.id },
    });
  };

  return (
    <ProjectsList
      showProjectBoundariesOnMap={
        currentOrganization?.esriConfig?.integrationTypeId != null &&
        currentOrganization.esriConfig.integrationTypeId !== EsriIntegrationTypes.NONE.id
      }
      isProjectsLoading={getProjectsStatus.loading}
      projects={projects && projects.projectsByOrganizationId?.items}
      onSearchProjects={handleSearchProjects}
      onAddProject={handleAddProject}
      addProjectStatus={addProjectStatus}
      onEditProject={handleEditProject}
      editProjectStatus={updateProjectStatus}
      onDeleteProject={handleDeleteProject}
      deleteProjectStatus={deleteProjectStatus}
      onAddPermitRegister={handleAddPermitRegister}
      addPermitRegisterStatus={addPermitRegisterStatus}
      onUpdatePermitRegister={handleUpdatePermitRegister}
      updatePermitRegisterStatus={updatePermitRegisterStatus}
      onDeletePermitRegister={handleDeletePermitRegister}
      deletePermitRegisterStatus={deletePermitRegisterStatus}
      onSetMaxProjectAssignments={handleSetMaxProjectAssignments}
      setMaxStatus={setMaxStatus}
      permitTypes={permitTypesData?.permitTypesByOrganizationId}
    ></ProjectsList>
  );
};

export default ProjectsListContainer;
